import React, { useState, useEffect } from 'react';
import { Box, Popover, Typography, List, ListItem, ListItemText, InputBase, Collapse, CircularProgress, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import SearchIcon from '../../icons/Search';
import ChevronUpIcon from '../../icons/ChevronUp';
import ChevronDownIcon from '../../icons/ChevronDown';
import UserIcon from '../../icons/User';
import LockIcon from '../../icons/Lock';
import LinkIcon from '../../icons/Link';
import { dashboardApi } from 'src/api/dashboardApi';

const useStyles = makeStyles({
  popover: {
    '& .MuiPaper-root': {
      width: '350px',
      maxHeight: '500px',
      overflow: 'hidden',
      borderRadius: '12px',
    },
  },
  header: {
    padding: '20px',
    borderBottom: '1px solid #e0e0e0',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#333',
    marginBottom: '16px',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '8px 12px',
    backgroundColor: '#fff',
  },
  searchInput: {
    flex: 1,
    fontSize: '14px',
    '&::placeholder': {
      color: '#999',
    },
  },
  searchIcon: {
    color: '#999',
    marginRight: '8px',
  },
  list: {
    padding: 0,
    maxHeight: '400px',
    overflowY: 'auto',
  },
  sectionHeader: {
    padding: '12px 20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: 500,
    color: '#333',
  },
  chevronIcon: {
    color: '#666',
  },
  sectionContent: {
    backgroundColor: '#fff',
  },
  listItem: {
    padding: '12px 20px 12px 20px',
    cursor: 'pointer',
    borderBottom: '1px solid #f5f5f5',
    '&:hover': {
      backgroundColor: '#f9f9f9',
    },
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  listItemText: {
    fontSize: '14px',
    color: '#333',
  },
  listItemActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  actionButton: {
    padding: '4px',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px 20px',
  },
  errorContainer: {
    padding: '20px',
    textAlign: 'center',
    color: '#f44336',
  },
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
    color: '#666',
  },
});

interface QuickLink {
    id: string;
    label: string;
    path: string;
    section: string;
    originalName: string;
    username: string;
    password: string;
}

interface ProjectSection {
    id: string;
    name: string;
    links: QuickLink[];
    expanded: boolean;
}

interface Environment {
    id: string;
    name: string;
    url: string;
    username: string;
    password: string;
}

interface ProjectData {
    id: string;
    name: string;
    environments: Environment[];
}

interface QuickLinksPopoverProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
}

const QuickLinksPopover: React.FC<QuickLinksPopoverProps> = ({ anchorEl, open, onClose }) => {
    const classes = useStyles();
    const [searchTerm, setSearchTerm] = useState('');
    const [projectSections, setProjectSections] = useState<ProjectSection[]>([]);
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);





    const transformApiDataToProjectSections = (projects: ProjectData[]): ProjectSection[] =>
        projects.map((project, index) => ({
            id: project.id,
            name: project.name,
            expanded: index === 0, // First project expanded by default
            links: project.environments.map(env => ({
                id: env.id,
                label: env.url, // Show URL instead of name
                path: env.url,
                section: project.id,
                originalName: env.name, // Store original name for copying
                username: env.username,
                password: env.password,
            }))
        }));

    const fetchProjectLinks = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await dashboardApi.getQuickLinks(0, 10);


            if (response.data && Array.isArray(response.data?.data)) {
                console.log("links response.1...", response.data.data);
                const projects = transformApiDataToProjectSections(response.data?.data);
                setProjectSections(projects);

                // Set first project as expanded by default
                const firstProjectId = projects[0]?.id;
                if (firstProjectId) {
                    setExpandedSections({ [firstProjectId]: true });
                }
            }
        } catch (err) {
            console.error('Error fetching project links:', err);
            setError('Failed to load project links');
        } finally {
            setLoading(false);
        }
    };

    // Fetch data from API
    useEffect(() => {
        if (open) {
            fetchProjectLinks();
        }
    }, [open]);

    const handleLinkClick = (originalName: string) => {
        // Copy the original name to clipboard
        navigator.clipboard.writeText(originalName).then(() => {
            // You could add a toast notification here if needed
            console.log('Copied to clipboard:', originalName);
        }).catch(err => {
            console.error('Failed to copy to clipboard:', err);
        });
        onClose();
    };

    const handleCopyUsername = (username: string, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent triggering the main link click
        navigator.clipboard.writeText(username).then(() => {
            console.log('Copied username to clipboard:', username);
        }).catch(err => {
            console.error('Failed to copy username to clipboard:', err);
        });
    };

    const handleCopyPassword = (password: string, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent triggering the main link click
        navigator.clipboard.writeText(password).then(() => {
            console.log('Copied password to clipboard:', password);
        }).catch(err => {
            console.error('Failed to copy password to clipboard:', err);
        });
    };

    const handleCopyName = (name: string, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent triggering the main link click
        navigator.clipboard.writeText(name).then(() => {
            console.log('Copied name to clipboard:', name);
        }).catch(err => {
            console.error('Failed to copy name to clipboard:', err);
        });
    };

    const handleSectionToggle = (sectionId: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const filteredSections = projectSections.map(section => ({
        ...section,
        links: section.links.filter(link =>
            link.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
    })).filter(section => section.links.length > 0);

    return (
        <Popover
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            className={classes.popover}
        >
            <Box className={classes.header}>
                <Typography className={classes.title}>
                    Quick Links
                </Typography>
                <Box className={classes.searchContainer}>
                    <SearchIcon className={classes.searchIcon} />
                    <InputBase
                        className={classes.searchInput}
                        placeholder="Search"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </Box>
            </Box>

            <List className={classes.list}>
                {loading ? (
                    <Box className={classes.loadingContainer}>
                        <CircularProgress size={24} />
                    </Box>
                ) : error ? (
                    <Box className={classes.errorContainer}>
                        <Typography variant="body2">{error}</Typography>
                    </Box>
                ) : filteredSections.length === 0 ? (
                    <Box className={classes.emptyState}>
                        <Typography variant="body2">No project links found</Typography>
                    </Box>
                ) : (
                    filteredSections.map((section) => (
                        <React.Fragment key={section.id}>
                            <ListItem
                                className={classes.sectionHeader}
                                onClick={() => handleSectionToggle(section.id)}
                            >
                                <Box className={classes.sectionTitle}>
                                    {section.name}
                                </Box>
                                {expandedSections[section.id] ? (
                                    <ChevronUpIcon className={classes.chevronIcon} />
                                ) : (
                                    <ChevronDownIcon className={classes.chevronIcon} />
                                )}
                            </ListItem>
                            <Collapse in={expandedSections[section.id]} timeout="auto" unmountOnExit>
                                <Box className={classes.sectionContent}>
                                    {section.links.map((link) => (
                                        <ListItem
                                            key={link.id}
                                            className={classes.listItem}
                                            onClick={() => handleLinkClick(link.originalName)}
                                        >
                                            <ListItemText
                                                primary={link.label}
                                                classes={{ primary: classes.listItemText }}
                                            />
                                            <Box className={classes.listItemActions}>
                                                <IconButton
                                                    className={classes.actionButton}
                                                    onClick={(e) => handleCopyName(link.originalName, e)}
                                                    title="Copy name"
                                                >
                                                    <LinkIcon color="primary" sx={{ fontSize: '16px' }} />
                                                </IconButton>
                                                <IconButton
                                                    className={classes.actionButton}
                                                    onClick={(e) => handleCopyUsername(link.username, e)}
                                                    title="Copy username"
                                                >
                                                    <UserIcon color="primary" sx={{ fontSize: '16px' }} />
                                                </IconButton>
                                                <IconButton
                                                    className={classes.actionButton}
                                                    onClick={(e) => handleCopyPassword(link.password, e)}
                                                    title="Copy password"
                                                >
                                                    <LockIcon color="primary" sx={{ fontSize: '16px' }} />
                                                </IconButton>
                                            </Box>
                                        </ListItem>
                                    ))}
                                </Box>
                            </Collapse>
                        </React.Fragment>
                    ))
                )}
            </List>
        </Popover>
    );
};

export default QuickLinksPopover;
